// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Snackbar,
  Tab, Tabs, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptIcon from '@mui/icons-material/ReceiptLong';
import CheckIcon from '@mui/icons-material/Check';
import KeyIcon from '@mui/icons-material/Key';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import InfoTab from '../components/foyer/OngletInfos';
import HomesTab from '../components/foyer/OngletLogements';
import RelativesTab from '../components/foyer/OngletProches';
import { isDummyAddress } from '../components/foyer/commun';
import { householdName, HOUSEHOLD_STATUSES } from '../components/foyer/vocabulaire';

/**
 * Fiche d'un foyer : tout ce qui le concerne, au même endroit. Un composant
 * par onglet (components/foyer/) ; la page charge la fiche, tient ce qui doit
 * survivre au rechargement qui suit chaque geste (recherches, logement déplié,
 * saisie en cours), le changement de statut et l'envoi d'un nouveau mot de
 * passe, ouvert depuis la barre d'actions comme depuis l'onglet des proches.
 *
 * Le détail technique d'un logement (intégration Home Assistant, identifiant
 * de webhook, jetons masqués) se trouve dans son panneau dépliant : on part
 * toujours d'un client pour le chercher.
 */

// Statuts que l'on peut choisir, distincts de ceux que l'on peut afficher.
// « installation_scheduled » reste dans HOUSEHOLD_STATUSES pour les fiches
// encore à cette valeur en base, mais le backend refuse cette cible en 400
// (CLIENT_STATUSES dans backend/app/api/routes/shared/clients.py).
const STATUSES = ['prospect', 'pending_payment', 'active', 'live', 'inactive'];

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snack, setSnack] = useState('');
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  // Nouveau mot de passe de l'app famille : il part au proche par courriel et
  // ne s'affiche jamais ici. `passwordSend` vaut null (fenêtre fermée) ou
  // { userId } du proche choisi ; `sendConfirmation` ne donne que l'adresse
  // du destinataire.
  const [passwordSend, setPasswordSend] = useState(null);
  const [passwordSendInProgress, setSendingPassword] = useState(false);
  const [sendConfirmation, setSendConfirmation] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [familyUsers, setFamilyUsers] = useState([]);
  // { property_id: santé de la détection }, pour la colonne « Détection ».
  const [detectorHealth, setDetectorHealth] = useState({});
  // Caméras du foyer et facturation (GET /clients/{id}/cameras) : le support y
  // voit combien de caméras le foyer paie sans ouvrir Stripe, peut en offrir
  // une (cameras_included) ou rejouer une synchronisation échouée.
  const [cameraInfo, setCameraInfo] = useState(null);
  // Saisie en cours du nombre de caméras incluses. null = « rien de saisi », le
  // champ affiche alors la valeur du serveur (et se remet à jour tout seul
  // après un rechargement, ce qu'un defaultValue ne ferait pas).
  const [includedDraft, setIncludedDraft] = useState(null);
  const [homeSearch, setHomeSearch] = useState('');
  const [relativeSearch, setRelativeSearch] = useState('');
  // Logement dont le panneau technique (webhook, jetons, script) est ouvert.
  const [openHome, setOpenHome] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const [cRes, rRes] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/rooms`),
      ]);
      const c = cRes.data;
      setClient(c);
      setRooms(rRes.data);
      try {
        const fRes = await api.get(`/mobile/users/by-client/${id}`);
        setFamilyUsers(fRes.data);
      } catch { setFamilyUsers([]); }
      // Santé de la détection de chute, par logement. Appel isolé : un 404 ne
      // doit pas empêcher l'affichage de la fiche client.
      try {
        const hRes = await api.get(`/detector-health/client/${id}`);
        const byProp = {};
        (hRes.data.properties || []).forEach(p => { byProp[p.property_id] = p; });
        setDetectorHealth(byProp);
      } catch { setDetectorHealth({}); }
      // Parc de caméras et son coût, isolé pour la même raison.
      try {
        const camRes = await api.get(`/clients/${id}/cameras`);
        setCameraInfo(camRes.data);
      } catch { setCameraInfo(null); }
    } catch {
      setError('Impossible de charger le client');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const doAction = async (actionFn, label) => {
    setActionLoading(label);
    try {
      await actionFn();
      setSnack(`${label} effectué avec succès`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || `Erreur: ${label}`);
    } finally {
      setActionLoading('');
    }
  };

  const handleStatusChange = async () => {
    await doAction(() => api.post(`/clients/${id}/status/${newStatus}`), 'Changement de statut');
    setStatusDialog(false);
  };

  const handleMarkPaid = () => doAction(() => api.post(`/clients/${id}/mark-paid`), 'Marqué payé');

  // Le bouton de la barre d'actions et celui de chaque ligne de proche ouvrent
  // la même confirmation. Le proche visé est présélectionné quand il n'y a pas
  // de choix à faire (un seul compte actif) ou qu'il a été désigné sur sa ligne.
  const openSendPassword = (relative) => {
    const activeItems = familyUsers.filter(u => u.is_active);
    setPasswordSend({ userId: relative?.id || (activeItems.length === 1 ? activeItems[0].id : '') });
  };

  // Le serveur tire le mot de passe et ne rend que l'adresse. Le proche est
  // toujours désigné : sans lui, un foyer de plusieurs comptes répondrait 409.
  const handleSendPassword = async () => {
    if (!passwordSend?.userId) return;
    setSendingPassword(true);
    try {
      const res = await api.post(`/clients/${id}/generate-app-password`,
        { mobile_user_id: passwordSend.userId });
      setPasswordSend(null);
      setSendConfirmation(`Nouveau mot de passe envoyé à ${res.data.sent_to}.`);
    } catch (e) {
      setPasswordSend(null);
      setError(e.response?.data?.detail || "Envoi du nouveau mot de passe impossible");
    } finally {
      setSendingPassword(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  if (!client) return <Alert severity="error">Client introuvable</Alert>;

  const isIndividual = client.client_type === 'individual';

  // Comptes qui peuvent recevoir un mot de passe : un compte désactivé ne se
  // connecte pas, le serveur le refuse en 409.
  const activeAccounts = familyUsers.filter(u => u.is_active);
  const passwordRecipient = activeAccounts.find(u => u.id === passwordSend?.userId) || null;

  // Options de la liste déroulante, qui peuvent dépasser les statuts choisissables.
  // Le dialogue s'ouvre sur le statut courant ; absent des options, MUI afficherait
  // un champ vide et journaliserait « out-of-range value ». Un statut hérité comme
  // « installation_scheduled » est donc réinjecté, mais désactivé : le backend
  // refuserait cette cible en 400.
  const statusChoices = STATUSES.includes(client.status)
    ? STATUSES
    : [client.status, ...STATUSES];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/clients')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">{householdName(client)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {isIndividual ? 'Particulier' : 'Professionnel'} · ID: {client.id}
          </Typography>
        </Box>
        <Chip
          label={HOUSEHOLD_STATUSES[client.status]?.label || client.status}
          color={HOUSEHOLD_STATUSES[client.status]?.color || 'default'}
        />
        <Button variant="outlined" size="small" startIcon={<EditIcon />}
          onClick={() => { setNewStatus(client.status); setStatusDialog(true); }}>
          Changer statut
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 1.5, mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {/* Un établissement se facture depuis Facturation, sur le parc de son
            site, là où se trouvent l'IBAN, le moyen de paiement et les
            mentions obligatoires. Rien pour un foyer : la vente aux
            particuliers est fermée. */}
        {client.client_type === 'professional' && (
          <Button size="small" variant="outlined" startIcon={<ReceiptIcon />}
            onClick={() => navigate('/facturation')}>
            Facturation
          </Button>
        )}
        <Button size="small" variant="outlined" color="success" startIcon={<CheckIcon />}
          disabled={!!actionLoading} onClick={handleMarkPaid}>
          Marquer payé
        </Button>
        <Button size="small" variant="outlined" color="warning" startIcon={<KeyIcon />}
          disabled={passwordSendInProgress} onClick={() => openSendPassword(null)}>
          Envoyer un nouveau mot de passe
        </Button>
      </Paper>

      {sendConfirmation && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSendConfirmation('')}>
          {sendConfirmation}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Informations" />
        <Tab label={`Logements (${rooms.length})`} />
        <Tab label={`Comptes famille (${familyUsers.length})`} />
      </Tabs>

      {tab === 0 && (
        <InfoTab
          clientId={id}
          client={client}
          comptesActifs={activeAccounts}
          cameraInfo={cameraInfo}
          onCameraInfo={setCameraInfo}
          includedDraft={includedDraft}
          onIncludedDraft={setIncludedDraft}
          onSnack={setSnack}
          onErreur={setError}
          onRecharger={load}
        />
      )}

      {tab === 1 && (
        <HomesTab
          clientId={id}
          rooms={rooms}
          detectorHealth={detectorHealth}
          recherche={homeSearch}
          onRecherche={setHomeSearch}
          logementOuvert={openHome}
          onLogementOuvert={setOpenHome}
          onSnack={setSnack}
          onErreur={setError}
          onRecharger={load}
        />
      )}

      {tab === 2 && (
        <RelativesTab
          clientId={id}
          familyUsers={familyUsers}
          recherche={relativeSearch}
          onRecherche={setRelativeSearch}
          envoiMdpEnCours={passwordSendInProgress}
          onEnvoyerMotDePasse={openSendPassword}
          onConfirmation={setSendConfirmation}
          onSnack={setSnack}
          onErreur={setError}
          onRecharger={load}
        />
      )}

      <Dialog open={statusDialog} onClose={() => setStatusDialog(false)}>
        <DialogTitle>Changer le statut</DialogTitle>
        <DialogContent sx={{ pt: 2, minWidth: 300 }}>
          <FormControl fullWidth>
            <InputLabel>Nouveau statut</InputLabel>
            <Select value={newStatus} label="Nouveau statut" onChange={e => setNewStatus(e.target.value)}>
              {statusChoices.map(s => (
                <MenuItem key={s} value={s} disabled={!STATUSES.includes(s)}>
                  {HOUSEHOLD_STATUSES[s]?.label || s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(false)}>Annuler</Button>
          {/* Désactivé tant que rien n'a changé : il enverrait sinon le statut
              hérité, que l'API rejette en 400. */}
          <Button variant="contained" onClick={handleStatusChange}
            disabled={!!actionLoading || !newStatus || newStatus === client.status}>
            {actionLoading === 'Changement de statut' ? <CircularProgress size={16} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!passwordSend} onClose={() => { if (!passwordSendInProgress) setPasswordSend(null); }}
        maxWidth="xs" fullWidth>
        <DialogTitle>Envoyer un nouveau mot de passe</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {activeAccounts.length === 0 ? (
            <Typography variant="body2">
              Ce foyer n&apos;a aucun compte dans l&apos;app : ajoutez d&apos;abord un proche
              (onglet « Comptes famille »).
            </Typography>
          ) : (
            <>
              {activeAccounts.length > 1 && (
                <FormControl fullWidth size="small">
                  <InputLabel>Proche</InputLabel>
                  <Select value={passwordSend?.userId || ''} label="Proche"
                    onChange={e => setPasswordSend({ userId: e.target.value })}>
                    {activeAccounts.map(u => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.full_name ? `${u.full_name} (${u.email})` : u.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {passwordRecipient && isDummyAddress(passwordRecipient.email) ? (
                <Typography variant="body2" color="error">
                  <strong>{passwordRecipient.email}</strong> est une adresse fictive : aucun courriel
                  ne lui parviendrait, et le mot de passe changerait pour personne. Le mot de passe
                  d&apos;un compte de démonstration se change dans le conteneur backend
                  (compte_demo_famille).
                </Typography>
              ) : passwordRecipient ? (
                <Typography variant="body2">
                  Un nouveau mot de passe sera créé et envoyé par courriel
                  à <strong>{passwordRecipient.email}</strong>, et à personne d&apos;autre : il ne
                  s&apos;affichera pas ici. L&apos;ancien cessera de fonctionner et le proche devra
                  se reconnecter dans l&apos;app.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Choisissez le proche qui recevra le nouveau mot de passe.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPasswordSend(null)} disabled={passwordSendInProgress}>Annuler</Button>
          <Button variant="contained" onClick={handleSendPassword}
            disabled={!passwordRecipient || isDummyAddress(passwordRecipient.email) || passwordSendInProgress}>
            {passwordSendInProgress ? <CircularProgress size={16} /> : 'Envoyer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')}
        message={snack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />

    </Box>
  );
}
